import { cn } from "@/lib/utils";

/**
 * The toolbar button's three states — and the **recorded parity break** (D7).
 *
 * The donor draws all of this with colour alone, and with the *same* colour
 * twice: `ToolbarIcon` (`RichTextEditor.tsx:344`) returns `primary.main` when a
 * command is toggled and `primary.main` again on `&:hover`, over a `gray`
 * resting glyph. So in the source, hovering an inactive Bold makes it look
 * exactly like an active one, and "this command applies where my caret is" has
 * no signal a person can read. Restoring the donor's greys made both states
 * visible; it could not make them distinguishable, because in the source they
 * are not.
 *
 * The owner ruled to break parity here and make active unmistakable. So:
 *
 *   - **resting** — the donor's `gray` glyph on no ground.
 *   - **hover** — a blue-tinted ground under a `primary.main` glyph. Keeps the
 *     donor's hover *colour* and adds a ground the donor has no equivalent for.
 *   - **active** — inverted: the glyph goes white on a solid `primary.main`
 *     chip. Nothing else in this toolbar is filled, so an active command reads
 *     at a glance and cannot be confused with a hover.
 *   - **disabled** — the donor's `lightgray`, and it overrides the active
 *     branch, because the Link control can be both at once (the caret inside a
 *     link with nothing selected).
 *
 * **The chip is glyph-tight, and that is part of the break's record** (#581).
 * D7 ruled on *states* — which ground goes under which glyph — and named no box
 * size; the ground it asks for was drawn on a 32px box, which is 4px of pad a
 * side more than the donor's control has. The donor's `ToolbarIcon` is a bare
 * `styled(SvgIcon)` with no padding at all (`RichTextEditor.tsx:344-358`), so
 * its box is MUI's `SvgIcon` default — `width/height: 1em` at
 * `fontSize: 'medium'` → `pxToRem(24)` (`SvgIcon.js:45-46`, `:75-78`) — and
 * with the row's 5px gap (`RichTextEditor.tsx:337`) that is the donor's 29px
 * pitch. The pad cost 8px of pitch per control and pushed the row tens of
 * pixels wide of the donor's 210px, so the box is now `size-6` and the ground
 * is exactly the glyph's bounding box.
 *
 * What D7 ruled is unchanged: active and hover still differ in **ground**, not
 * only in ink. Only the ground's generosity changed, and it changed *toward*
 * the source — the donor has no ground at all, so a glyph-tight chip is a
 * smaller departure than a padded one. The alternative was keeping D7's
 * incidental padding at the price of the pitch, which is not a trade an agent
 * may make for the owner — so it was not made: the tight chip is **owner-ruled
 * (#581 decision round D1 = a, 2026-09-16)**, over keeping the 32px chip and
 * reopening the pitch, and over bleeding the ground outside the box with a
 * pseudo-element. The ground is the glyph's box because the owner said so, not
 * because the geometry forced it.
 *
 * Its own module for the same reason `dialog-portal-container.ts` is one: a
 * non-component export out of `rich-text-field.tsx` trips
 * `react-refresh/only-export-components`, and lint runs at zero warnings. Being
 * exported is what lets the break be pinned — the substance of it is that
 * active and hover differ in *ground* and not only in ink, which is checkable
 * without a stylesheet.
 */
export function toolbarButtonClasses(active: boolean): string {
  return cn(
    // `cursor: disabled ? 'not-allowed' : 'pointer'` — `ToolbarIcon` sets both
    // explicitly (`RichTextEditor.tsx:351`), unlike the link card's two icons,
    // which keep the hand even when the confirm is off.
    // `size-6` is the donor's control box, not a chip size chosen here — see
    // the glyph-tight paragraph above for the derivation and what it means for
    // D7. The Link control swaps `flex` for `inline-block` (`ToolbarButton`'s
    // `inline`) to stand on a line box as the donor's inline `SvgIcon` does;
    // its glyph fills the 24px box, so it needs no centring.
    "flex size-6 cursor-pointer items-center justify-center rounded-[4px] transition-colors",
    active
      ? "bg-lennar-blue-600 hover:bg-lennar-blue-hover text-white"
      : "text-lennar-icon-resting hover:bg-lennar-primary-shade hover:text-lennar-blue-600",
    "disabled:bg-transparent disabled:hover:bg-transparent",
    "disabled:text-lennar-icon-disabled disabled:hover:text-lennar-icon-disabled disabled:cursor-not-allowed",
  );
}
